import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoomType } from "building-types/rooms/roomTypesEnum";
import type { Table } from "common/data/table";
import type { RoomIdToRoomSetIndexLookup, RoomSetWithLevelId } from "doc/rooms/roomSetTypes";
import { polyLine2Area } from "geom/polyLine2";
import { isGarageDoorOperationType } from "../doors/doorUtils";
import type { RoomSetSpaceTypesTable, SpaceClassifierTables } from "./roomAndSpaceTypes/electricalSpaceTypes";
import { SpaceAccessRankingTable } from "./roomAndSpaceTypes/electricalSpaceTypes";
import { getRoomSetSpaceTypes } from "./roomAndSpaceTypes/getRoomSetSpaceTypes";
import { optimizeSpaceClassifierTables as optimizeSpaceClassifiers } from "./roomAndSpaceTypes/optimizeSpaceClassifierTables";

type RoomSetsSpaceTypes = { [roomSetIndex: number]: RoomSetSpaceTypesTable };

interface RoomSetInfo {
  readonly spaceTypesTable: RoomSetSpaceTypesTable;
  readonly doorScores: DoorScore[];
  readonly roomSetArea: number;
  readonly levelId: string;
}

export type RoomSetInfoTable = { [roomSetIndex: number]: RoomSetInfo };

export function roomSetInfoTableNew(
  roomSets: RoomSetWithLevelId[],
  doors: Table<GeneratedDoorPlacement>,
  roomTable: Table<Room>,
  roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup,
): RoomSetInfoTable {
  const classifierTables = optimizeSpaceClassifiers();

  const roomSetDoorScoresTable: RoomSetInfoTable = [];

  const { roomSetAccessScoreTable, roomSetsSpaceTypesTable } = roomSetAccessScoreLookup(
    roomSets,
    roomTable,
    classifierTables,
  );

  for (const { id, roomSet, levelId } of roomSets) {
    let roomSetArea = 0;

    for (const { id } of roomSet) {
      const room = roomTable[id];
      if (!room || !room.outline || room.outline.length < 6) {
        continue;
      }
      roomSetArea += polyLine2Area(room.outline);
    }

    const doorScores = getDoorAccessScoresInARoomSet(doors, roomIdToRoomSetIndexLookup, id, roomSetAccessScoreTable);
    const spaceTypesTable = roomSetsSpaceTypesTable[id];
    roomSetDoorScoresTable[id] = { spaceTypesTable, doorScores, roomSetArea, levelId };
  }

  return roomSetDoorScoresTable;
}

function roomSetAccessScoreLookup(
  roomSets: RoomSetWithLevelId[],
  roomTable: Table<Room>,
  classifierTables: SpaceClassifierTables,
): {
  roomSetAccessScoreTable: RoomSetAccessScoreTable;
  roomSetsSpaceTypesTable: RoomSetsSpaceTypes;
} {
  const roomSetAccessScoreTable: RoomSetAccessScoreTable = {};
  const roomSetsSpaceTypesTable: RoomSetsSpaceTypes = {};

  for (const { roomSet, id } of roomSets) {
    const { majoritySpaceType, roomSpaceTypes } = getRoomSetSpaceTypes(roomSet, roomTable, classifierTables);
    roomSetAccessScoreTable[id] = SpaceAccessRankingTable[majoritySpaceType];
    roomSetsSpaceTypesTable[id] = roomSpaceTypes;
  }

  return { roomSetAccessScoreTable, roomSetsSpaceTypesTable };
}

type RoomSetAccessScoreTable = { [roomSetIndex: number]: number };
export interface DoorScore {
  doorId: string;
  score: number;
  setIndex: number;
  oppSetIndex: number;
  roomType: RoomType;
}

function getDoorAccessScoresInARoomSet(
  doors: Table<GeneratedDoorPlacement>,
  roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup,
  setIndex: number,
  lowestScoreTable: RoomSetAccessScoreTable,
): DoorScore[] {
  const doorScores: DoorScore[] = [];

  for (const id in doors) {
    const door = doors[id];
    if (isGarageDoorOperationType(door)) {
      continue;
    }
    const { id: doorId, frontRoom, backRoom } = door;
    if (frontRoom && roomIdToRoomSetIndexLookup[frontRoom.id] === setIndex && backRoom) {
      const roomSetIndex = roomIdToRoomSetIndexLookup[backRoom.id];
      if (roomSetIndex === undefined || roomSetIndex === setIndex) {
        continue;
      }
      const score = lowestScoreTable[roomSetIndex];
      doorScores.push({
        doorId,
        score,
        setIndex: roomSetIndex,
        roomType: backRoom.type,
        oppSetIndex: roomIdToRoomSetIndexLookup[frontRoom.id],
      });
    } else if (backRoom && roomIdToRoomSetIndexLookup[backRoom.id] === setIndex && frontRoom) {
      const roomSetIndex = roomIdToRoomSetIndexLookup[frontRoom.id];
      if (roomSetIndex === undefined || roomSetIndex === setIndex) {
        continue;
      }
      const score = lowestScoreTable[roomSetIndex];
      doorScores.push({
        doorId,
        score,
        setIndex: roomSetIndex,
        roomType: frontRoom.type,
        oppSetIndex: roomIdToRoomSetIndexLookup[backRoom.id],
      });
    }
  }

  doorScores.sort((a, b) => a.score - b.score);

  return doorScores;
}
